import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from "framer-motion";
import searchImg from "../../assets/images/icon/search.png";
import ApiService from '../../service/ApiService';
import { apiPaths } from '../../service/apiPaths';
import { Link } from 'react-router-dom';
import { FileSearch, Type } from 'lucide-react';

const SearchProducts = ({ closeSearch }) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredProducts, setFilteredProducts] = useState(null);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1); // Backend expects page starting from 1
    const [hasData, setHasData] = useState(false);
    const [pagination, setPagination] = useState(null);
    const containerRef = useRef(null);

    const handleType = (e) => {
        setHasData(true);
        setCurrentPage(1); // Reset to page 1 for new search
        setSearchQuery(e.target.value);
        setFilteredProducts(null); // Clear previous results
        setPagination(null);
    }

    const handleKeyDown = async (isNextPage = false) => {
        isNextPage ? 
            console.log("Pagination API call for value:", searchQuery, "for page:", currentPage) : 
            console.log("Initial search API call");

        if (!hasData) return;

        // Show loading for initial search or when no products exist yet
        if (!filteredProducts || filteredProducts.length === 0 || (isNextPage && searchQuery)) {
            setLoading(true);
        }

        try {
            const response = await ApiService({
                method: "GET",
                endpoint: `${apiPaths.SearchProducts}?search=${searchQuery}&page=${currentPage}&limit=12`,
            });

            // Backend returns: { products: [], pagination: {...} }
            const { products = [], pagination: paginationInfo } = response?.data || {};
            
            setPagination(paginationInfo);

            // Check if we have more data based on pagination info
            if (!paginationInfo?.hasNextPage) {
                setHasData(false);
            }
            
            if (isNextPage) {
                // Append new products for pagination
                setFilteredProducts((prev) => [...(prev || []), ...products]);
            } else {
                // Set new products for initial search
                setFilteredProducts(products);
            }
        
        } catch (error) {
            console.error("Search error:", error);
            setFilteredProducts([]);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, []);

    useEffect(() => {
        console.log("Filtered products:", filteredProducts);
        console.log("Pagination:", pagination);
    }, [filteredProducts, pagination]);

    useEffect(() => {
        const handleScroll = () => {
            const container = containerRef.current;
            if (container) {
                const { scrollTop, scrollHeight, clientHeight } = container;

                // Check if scrolled to the bottom and we have more data
                if (scrollTop + clientHeight >= scrollHeight - 50 && 
                    !loading && 
                    hasData && 
                    pagination?.hasNextPage) {
                    setCurrentPage((prev) => prev + 1);
                }
            }
        };

        const container = containerRef.current;
        if (container) {
            container.addEventListener("scroll", handleScroll);
        }

        return () => {
            if (container) container.removeEventListener("scroll", handleScroll);
        };
    }, [loading, hasData, pagination]);

    useEffect(() => {
        if (currentPage > 1) {
            handleKeyDown(true);
        }
    }, [currentPage]);
    
    useEffect(() => {
        let timeoutId;
        if (searchQuery.trim()) {
            timeoutId = setTimeout(() => {
                console.log("Search function called");
                handleKeyDown();
            }, 700);
        } else {
            setFilteredProducts(null);
            setPagination(null);
            setHasData(false);
        }

        return () => timeoutId && clearTimeout(timeoutId);
    }, [searchQuery]);

    // Helper function to get product price display
    const getProductPrice = (product) => {
        if (product.onSale && product.minPrice) {
            return `₹${product.minPrice}`;
        }
        return product.maxPrice ? `₹${product.minPrice} - ₹${product.maxPrice}` : `₹${product.minPrice || 0}`;
    };

    // Animation variants for empty state messages
    const messageVariants = {
        hidden: { scale: 0.8, opacity: 0 },
        visible: { 
            scale: 1, 
            opacity: 1,
            transition: { 
                type: "spring",
                stiffness: 400,
                damping: 15
            }
        }
    };

    return (
        <motion.div
            initial={{ y: -300, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.2 }}
            className="md:w-[80%] h-full md:max-h-[500px] mx-auto rounded-b-xl shadow-sm shadow-white border-[1px] border-gray-50 relative bg-[#fff] z-30 lg:px-[10%] px-[20px]"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="w-full max-w-[991px] flex justify-center items-center py-4 mx-auto relative">
                <img src={searchImg} alt="img" className="w-[32px] h-[19px] object-contain absolute left-3" />
                <input 
                    type="text" 
                    placeholder="Search..." 
                    placeholderTextColor="#000" 
                    onChange={(e) => { handleType(e) }} 
                    className="w-full h-[60px] px-12 outline-none border-[1px] border-gray-200 rounded-[40px]" 
                />
            </div>
            
            {/* Products container */}
            <div ref={containerRef} className='overflow-y-auto h-full md:max-h-[400px] max-h-full rounded-lg scrollbar-none py-4'>
                <AnimatePresence mode="wait">
                    {loading && (!filteredProducts || filteredProducts.length === 0) ? (
                        // Loading skeleton for initial search
                        <motion.div
                            key="loading"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <div className='flex gap-3 items-center border-[1px] border-gray-200 rounded-lg p-4 skeleton h-[100px] mb-4'>
                                <div className='border-[1px] border-[#ccc] rounded-lg p-2 skeleton h-[80px] w-[80px]'></div>
                                <div className='p-2 flex flex-col justify-between skeleton h-[80px] border-[1px] border-[#ccc] w-full rounded-lg'>
                                    <div className='skeleton h-[15px] border-[1px] rounded-sm border-[#ccc] w-[20%]'></div>
                                    <div className='font-medium skeleton h-[15px] rounded-sm border-[1px] border-[#ccc] w-[50%]'></div>
                                </div>
                            </div>
                            <div className='flex gap-3 items-center border-[1px] border-gray-200 rounded-lg p-4 skeleton h-[100px]'>
                                <div className='border-[1px] border-[#ccc] rounded-lg p-2 skeleton h-[80px] w-[80px]'></div>
                                <div className='p-2 flex flex-col justify-between skeleton h-[80px] border-[1px] border-[#ccc] w-full rounded-lg'>
                                    <div className='skeleton h-[15px] border-[1px] rounded-sm border-[#ccc] w-[20%]'></div>
                                    <div className='font-medium skeleton h-[15px] rounded-sm border-[1px] border-[#ccc] w-[50%]'></div>
                                </div>
                            </div>
                        </motion.div>
                    ) : filteredProducts && filteredProducts.length > 0 ? (
                        <motion.div
                            key="results"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6'>
                                {filteredProducts.map((item, index) => (
                                    <Link 
                                        to={`/product/${item._id}`} 
                                        onClick={closeSearch} 
                                        key={`${item._id}-${index}`}
                                        className='flex items-center border-[1px] border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-300'
                                    >
                                        <div className='border-[1px] border-gray-200 rounded-lg p-2 mr-3'>
                                            <img 
                                                src={item.mainImage || (item.colorOptions && item.colorOptions[0]?.image) || '/placeholder-image.jpg'} 
                                                alt={item.name || item.productName} 
                                                className='w-[80px] h-[60px] object-contain' 
                                                onError={(e) => {
                                                    e.target.src = '/placeholder-image.jpg';
                                                }}
                                            />
                                        </div>
                                        <div className='flex-1'>
                                            <h3 className="font-medium text-gray-800 mb-1">
                                                {item.name || item.productName}
                                            </h3>
                                            <p className="text-sm text-gray-600 mb-1">
                                                {item.brand && `Brand: ${item.brand}`}
                                            </p>
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold text-gray-900">
                                                    {getProductPrice(item)}
                                                </span>
                                                {item.onSale && (
                                                    <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                                                        On Sale
                                                    </span>
                                                )}
                                            </div>
                                            {item.colorOptions && item.colorOptions.length > 0 && (
                                                <div className="flex gap-1 mt-2">
                                                    {item.colorOptions.slice(0, 3).map((color, colorIndex) => (
                                                        <div
                                                            key={colorIndex}
                                                            className="w-4 h-4 rounded-full border border-gray-300"
                                                            style={{ backgroundColor: color.hexCode || '#gray' }}
                                                            title={color.name}
                                                        />
                                                    ))}
                                                    {item.colorOptions.length > 3 && (
                                                        <span className="text-xs text-gray-500 self-center">
                                                            +{item.colorOptions.length - 3} more
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </Link>
                                ))}
                            </div>
                            
                            {/* Loading indicator for pagination */}
                            {loading && filteredProducts.length > 0 && (
                                <div className="flex justify-center py-4">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                                </div>
                            )}
                            
                            {/* End of results message */}
                            {!loading && pagination && !pagination.hasNextPage && filteredProducts.length > 0 && (
                                <div className="text-center py-4 text-gray-500">
                                    <p>No more products to show</p>
                                </div>
                            )}
                        </motion.div>
                    ) : filteredProducts?.length === 0 ? (
                        // No results found
                        <motion.div
                            key="not-found"
                            className="flex flex-col items-center justify-center py-12"
                            variants={messageVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            <FileSearch size={70} className="text-gray-400 mb-4" />
                            <motion.div
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1, transition: { delay: 0.2 } }}
                                className="text-center"
                            >
                                <h3 className="text-xl font-medium text-gray-700 mb-2">No Results Found</h3>
                                <p className="text-gray-500">
                                    We couldn't find any products matching "{searchQuery}".
                                </p>
                                <p className="text-gray-500">
                                    Try checking your spelling or using different keywords.
                                </p>
                            </motion.div>
                        </motion.div>
                    ) : (
                        // Initial state - prompt to search
                        <motion.div
                            key="type-first"
                            className="flex flex-col items-center justify-center py-12"
                            variants={messageVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            <Type size={70} className="text-gray-400 mb-4" />
                            <motion.div
                                initial={{ y: 20, opacity: 0 }}
                                animate={{ y: 0, opacity: 1, transition: { delay: 0.2 } }}
                                className="text-center"
                            >
                                <h3 className="text-xl font-medium text-gray-700 mb-2">Search Products</h3>
                                <p className="text-gray-500">
                                    Type a keyword in the search box above to find products.
                                </p>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </motion.div>
    );
}

export default SearchProducts;